import { useMutation } from '@tanstack/react-query';
import { ResourcelessPermissionId } from '@upright/common/iam/privilege/permission/types';
import { putUnifiedShellOptOut } from '@upright/ui/api/iam/users';
import { useHasResourcelessPrivilege } from '@upright/ui/api/iam/privilege/query-options';
import { errorToast } from '@upright/ui/common/generic/components/toast';
import { useGetLoggedInUser, useIsTeam, useViewSetting } from '@upright/ui/common/generic/hooks';
import { setViewSetting, storeUserExtraData } from '@upright/ui/untidy/redux/actions/actions';
import { useDispatch } from 'react-redux';
import { unifiedShellChoice } from './unified-shell-choice';
import { useUnifiedShellAudience } from './use-unified-shell-audience';

/**
 * Whether the current user gets the unified shell on the investor side — the white
 * top strip, the Primer-pane rail and the launcher landing at `/` — instead of the
 * dark `BouToolHeader`, `BouPageWithFrame` and the assets table (Unified UI Phase 1,
 * PLT-6530).
 *
 * This is the one gate the header switch (`root/main/header-maybe.tsx`), the page
 * frame and `LoggedInHomeViewSwitch` (`home-views/index.tsx`) all read, so the three
 * cannot disagree about which shell a user is in. Do not re-derive the decision at a
 * call site from `organization.type` or a user group.
 *
 * The company front page takes the same shell (PLT-6943): the launcher at `/` with company
 * prompts, a *My company* page for the old company dashboard, and a company variant of the rail
 * (`UnifiedCompanyNavigation`). Which of the two a user gets follows their organisation's type, not
 * their groups (PLT-8105); `useUnifiedShellAudience` decides.
 *
 * Three answers:
 * - No audience (DMA and Arvo organisations, and users outside every organisation whose groups give
 *   them no investor front page): `false`, without asking for the privilege. Those shells are their
 *   own and this gate does not touch them.
 * - Team users: the team-only `internalAdmin.unifiedShell` View setting, on by default
 *   (PLT-7106). A team user who wants to see what a not-yet-opted-in customer sees turns
 *   it off (the row in the start menu at the bottom left, View settings, or the "New
 *   interface" row of the account menu), and that choice is remembered. The setting is the
 *   whole gate for the team: the privilege's team-shortcut would answer `true`
 *   unconditionally, which is why team users are resolved first and the privilege is not
 *   consulted for them.
 * - Everyone else: the `USE_UNIFIED_SHELL` resourceless privilege, granted per
 *   organisation from the privileges admin UI, unless the user has turned the shell off for
 *   themselves with the "New interface" row of the account menu (PLT-7968). That choice is
 *   stored on the user, so it follows them to another browser.
 *
 * `loading` is `true` while a customer's privilege query is in flight. A consumer that
 * would otherwise paint the old chrome and swap it out a moment later renders nothing
 * until it resolves. The team and no-audience answers never load.
 */
export function useUnifiedShell(): { enabled: boolean; loading: boolean } {
  const { offered, chosen, loading } = useUnifiedShellChoice();
  return { enabled: offered && chosen, loading };
}

/**
 * The "New interface" switch of the account menus, in both shells (PLT-7968): whether to show it,
 * whether it is on, whether a change is being saved, and how to flip it.
 *
 * The switch appears only where the shell is offered, so a user whose organisation does not have
 * it never sees a switch that does nothing. For a team user it writes the same View setting as
 * the start menu's "Unified UI" row, so the two cannot disagree; for everyone else it saves the
 * choice on the user and swaps the shell once the server has it, with `pending` true meanwhile.
 */
export function useUnifiedShellToggle(): {
  available: boolean;
  enabled: boolean;
  pending: boolean;
  setEnabled: (enabled: boolean) => void;
} {
  const dispatch = useDispatch();
  const { offered, chosen, isTeam } = useUnifiedShellChoice();
  const { mutate, isPending } = useMutation({
    mutationFn: putUnifiedShellOptOut,
    onSuccess: ({ unifiedShellOptOut }) => {
      dispatch(storeUserExtraData({ unifiedShellOptOut }));
    },
    // The switch springs back and the shell stays as it was, so without this a failed save looks
    // like a click that did nothing.
    onError: () => {
      errorToast('Couldn’t switch the interface. Please try again.');
    },
  });

  return {
    available: offered,
    enabled: chosen,
    pending: isPending,
    setEnabled: enabled => {
      if (isTeam) {
        dispatch(setViewSetting('internalAdmin.unifiedShell', enabled));
      } else {
        mutate(!enabled);
      }
    },
  };
}

/** The inputs of `unifiedShellChoice`, which holds the rule. Only a customer's answer loads. */
function useUnifiedShellChoice(): { offered: boolean; chosen: boolean; loading: boolean; isTeam: boolean } {
  const audience = useUnifiedShellAudience();
  const isTeam = useIsTeam();
  const user = useGetLoggedInUser();
  const teamSetting = useViewSetting('internalAdmin.unifiedShell');
  const { hasPrivilege, loading } = useHasResourcelessPrivilege(ResourcelessPermissionId.USE_UNIFIED_SHELL);

  const { offered, chosen } = unifiedShellChoice({
    audience,
    isTeam,
    teamSetting,
    hasPrivilege,
    extraData: user?.extraData,
  });
  return { offered, chosen, loading: audience !== null && !isTeam && loading, isTeam };
}
